import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import { auth, db } from './firebase.js'

type ActorBindingInput = {
  surface?: unknown
  identifier?: unknown
}

type ActorRecord = {
  actorId?: unknown
  bindings?: ActorBindingInput[]
  createdAt?: unknown
  displayName?: unknown
  email?: unknown
  role?: unknown
  workspaceIds?: unknown[]
}

type EnsureActorOptions = {
  bindings?: ActorBindingInput[]
  role?: unknown
  workspaceIds?: unknown[]
}

function t(value: unknown): string {
  return String(value ?? '').trim()
}

function normalizeIdentifier(value: unknown): string {
  return t(value).toLowerCase()
}

function sanitizeBindings(bindings: ActorBindingInput[] = []) {
  return (Array.isArray(bindings) ? bindings : [])
    .map((binding) => ({
      surface: t(binding?.surface).toLowerCase(),
      identifier: t(binding?.identifier),
      normalizedIdentifier: normalizeIdentifier(binding?.identifier),
    }))
    .filter((binding) => binding.surface && binding.identifier)
}

export async function callHostedTool(name: string, args: Record<string, unknown> = {}) {
  const token = await auth.currentUser?.getIdToken?.()
  const mcpUrl = t(import.meta.env.VITE_FUNCTIONS_MCP_URL)
  if (!token || !mcpUrl) {
    throw new Error('binding_resolver_not_configured')
  }
  const response = await fetch(mcpUrl, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      method: 'tools/call',
      params: {
        name,
        arguments: args
      }
    })
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || payload?.ok !== true) {
    throw new Error(t(payload?.error) || `${name}_failed`)
  }
  return payload.result
}

export async function ensureActorForUser(user: { uid?: string, displayName?: string | null, email?: string | null }, options: EnsureActorOptions = {}) {
  if (!user?.uid) {
    return null
  }

  const actorId = user.uid
  const canonicalRef = doc(db, 'actors', actorId)
  const canonicalSnap = await getDoc(canonicalRef)
  let existing = (canonicalSnap.exists() ? canonicalSnap.data() : {}) as ActorRecord

  if (!canonicalSnap.exists()) {
    const existingByUid = await getDocs(
      query(collection(db, 'actors'), where('uid', '==', user.uid), limit(1))
    )
    const legacyDoc = existingByUid.docs.find((candidate) => candidate.id !== actorId)
      || existingByUid.docs[0]
    existing = (legacyDoc?.data() || {}) as ActorRecord
  }

  const workspaceIds = Array.from(
    new Set([...(existing.workspaceIds || []), ...(options.workspaceIds || [])].map(t).filter(Boolean))
  )
  const bindings = sanitizeBindings([...(existing.bindings || []), ...(options.bindings || [])])

  const payload = {
    actorId,
    uid: user.uid,
    displayName: t(user.displayName) || t(existing.displayName),
    email: t(user.email) || t(existing.email),
    role: t(options.role) || t(existing.role) || 'owner',
    bindings,
    workspaceIds,
    updatedAt: serverTimestamp(),
    createdAt: existing.createdAt ?? serverTimestamp(),
  }

  await setDoc(canonicalRef, payload, { merge: true })
  return { ...payload, actorId }
}

export async function listActorWorkspaces(actorId: string) {
  const actorRef = doc(db, 'actors', t(actorId))
  const snap = await getDoc(actorRef)
  const data = snap.exists() ? snap.data() : {}
  return Array.isArray(data.workspaceIds) ? data.workspaceIds.map(t).filter(Boolean) : []
}
